/**
 * Multi-select & group-move ("verschieben") math — pure functions over the
 * event index. No DOM, no React: everything here is unit-testable and runs
 * in O(selected events) so 60-column days stay instant.
 *
 * Semantics (Excel-like, see plan doc "Multi-select & group move"):
 *  - A cell RANGE selects every event that intersects it (columns × time).
 *  - Ctrl/Cmd+click XORs single events into the working set.
 *  - "v" picks the movable subset up; a delta (columns, slots) previews the
 *    placement; "v"/Enter/click places, Escape cancels.
 */
import {
  MCCellPos,
  MCCellRange,
  MCEvent,
  MCEventMove,
  MCGridConfig,
  MCResource,
} from "./types";
import { MCEventIndex, eventsForResource } from "./eventIndex";
import { clampSlot } from "./gridGeometry";

/** Normalized rectangle of a range (inclusive columns, [start,end) minutes). */
export interface MCRangeRect {
  colStart: number;
  colEnd: number;
  minStart: number;
  minEnd: number;
}

export const rangeRect = (range: MCCellRange, cfg: MCGridConfig): MCRangeRect => {
  const colStart = Math.min(range.anchor.resourceIndex, range.head.resourceIndex);
  const colEnd = Math.max(range.anchor.resourceIndex, range.head.resourceIndex);
  const minStart = Math.min(range.anchor.slotMin, range.head.slotMin);
  const minEnd = Math.max(range.anchor.slotMin, range.head.slotMin) + cfg.slotMin;
  return { colStart, colEnd, minStart, minEnd };
};

/** All event ids intersecting the range. */
export const eventIdsInRange = (
  index: MCEventIndex,
  resources: ReadonlyArray<MCResource>,
  range: MCCellRange,
  cfg: MCGridConfig,
): Set<string> => {
  const rect = rangeRect(range, cfg);
  const ids = new Set<string>();
  for (let col = rect.colStart; col <= rect.colEnd; col++) {
    const resource = resources[col];
    if (!resource) continue;
    for (const event of eventsForResource(index, resource.id)) {
      if (event.startMin >= rect.minEnd) break; // sorted by startMin
      if (event.endMin > rect.minStart) ids.add(event.id);
    }
  }
  return ids;
};

/**
 * True when the event can be group-moved: a concrete same-day slot bound to a
 * therapist column. Breaks/working-hours/availability have their own edit
 * flows, and multi-day slots (LeaveOfAbsence…) are only clamped into the day
 * grid for display — retiming them from here would corrupt their real span.
 */
export const isMovableEvent = (event: MCEvent): boolean => {
  if (event.eventType !== "appointment" && event.eventType !== "timeslot") return false;
  if (event.timeSlotType === "LunchBreak" || event.timeSlotType === "LeaveOfAbsence") {
    return false;
  }
  if (event.isDraft) return false;
  // Locked appointments / non-editable events (adapter sets editable: false).
  if (event.raw.editable === false) return false;
  const raw = event.raw;
  const start = raw.start ? new Date(raw.start as string | number | Date) : null;
  const end = raw.end ? new Date(raw.end as string | number | Date) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }
  if (end.getTime() - start.getTime() >= 24 * 60 * 60 * 1000) return false;
  if (
    start.getFullYear() !== end.getFullYear() ||
    start.getMonth() !== end.getMonth() ||
    start.getDate() !== end.getDate()
  ) {
    // spans midnight → clamped rendering, not safely retimeable from the grid
    return false;
  }
  const timeSlotId = event.raw.extendedProps?.timeSlotId ?? event.raw.id;
  return Boolean(timeSlotId);
};

/** The movable subset of a selection, in stable index order. */
export const movableEvents = (
  index: MCEventIndex,
  ids: Iterable<string>,
): MCEvent[] => {
  const events: MCEvent[] = [];
  for (const id of ids) {
    const event = index.byId.get(id);
    if (event && isMovableEvent(event)) events.push(event);
  }
  events.sort((a, b) => a.startMin - b.startMin || a.resourceId.localeCompare(b.resourceId));
  return events;
};

export interface MCMovePlan {
  valid: boolean;
  moves: MCEventMove[];
}

/**
 * "Solid" events block drops onto them — the index-math mirror of the old
 * calendar's `eventOverlap` policy. Working hours, lunch breaks, cancelled
 * days and availability are containers/underlays an event may land on;
 * appointments and concrete time slots are not. (Background fills and hidden
 * group-patient slots never reach the foreground index at all.)
 */
export const isSolidEvent = (event: MCEvent): boolean =>
  event.eventType !== "working-hours" &&
  event.eventType !== "availability" &&
  event.timeSlotType !== "LunchBreak" &&
  event.timeSlotType !== "WorkingHours" &&
  event.timeSlotType !== "CancelledDay";

/**
 * True when [startMin, endMin) on a resource overlaps a solid event not in
 * `excludeIds`. O(log n + k) via the per-resource sorted list.
 */
export const hasSolidOverlap = (
  index: MCEventIndex,
  resourceId: string,
  startMin: number,
  endMin: number,
  excludeIds?: ReadonlySet<string>,
): boolean => {
  for (const event of eventsForResource(index, resourceId)) {
    if (event.startMin >= endMin) break; // sorted by startMin
    if (event.endMin <= startMin) continue;
    if (excludeIds?.has(event.id)) continue;
    if (isSolidEvent(event)) return true;
  }
  return false;
};

/**
 * Resolve a delta into concrete per-event targets — computed from the
 * transaction's CAPTURED sources, never from live index lookups, so a
 * snapshot rebuild mid-drag cannot blank or corrupt the plan. The plan is
 * invalid when any event would land outside the
 * column range, outside the visible day, or on top of a solid event
 * (targets are NOT silently clamped — the ghost shows exactly what placement
 * would do).
 */
export const planMove = (
  index: MCEventIndex,
  resources: ReadonlyArray<MCResource>,
  sources: ReadonlyArray<MCEvent>,
  deltaCols: number,
  deltaSlots: number,
  cfg: MCGridConfig,
): MCMovePlan => {
  const indexByResourceId = new Map<string, number>();
  resources.forEach((r) => indexByResourceId.set(r.id, r.index));

  // Both the captured id and the id currently at that slot key are excluded
  // from overlap checks, so a mid-drag optimistic patch/refresh of a moved
  // event never makes it block its own placement.
  const movedIds = new Set<string>();
  const movedSlotKeys = new Set<string>();
  for (const source of sources) {
    movedIds.add(source.id);
    const key = source.raw.extendedProps?.timeSlotId;
    if (key) movedSlotKeys.add(key);
  }
  if (movedSlotKeys.size > 0) {
    for (const live of index.events) {
      const key = live.raw.extendedProps?.timeSlotId;
      if (key && movedSlotKeys.has(key)) movedIds.add(live.id);
    }
  }

  const deltaMin = deltaSlots * cfg.slotMin;
  const moves: MCEventMove[] = [];
  let valid = true;

  for (const event of sources) {
    const col = indexByResourceId.get(event.resourceId);
    if (col == null) continue;

    const targetCol = col + deltaCols;
    const target = resources[targetCol];
    const targetStartMin = event.startMin + deltaMin;
    const targetEndMin = event.endMin + deltaMin;

    if (
      !target ||
      targetStartMin < cfg.dayStartMin ||
      targetEndMin > cfg.dayEndMin
    ) {
      valid = false;
    } else if (
      isSolidEvent(event) &&
      hasSolidOverlap(index, target.id, targetStartMin, targetEndMin, movedIds)
    ) {
      valid = false;
    }

    moves.push({
      event,
      targetResourceId: target?.id ?? event.resourceId,
      targetStartMin,
      targetEndMin,
    });
  }

  return { valid: valid && moves.length > 0, moves };
};

/** Clamp a delta so the WHOLE picked block stays inside the grid (keyboard nudge). */
export const clampDelta = (
  resources: ReadonlyArray<MCResource>,
  sources: ReadonlyArray<MCEvent>,
  deltaCols: number,
  deltaSlots: number,
  cfg: MCGridConfig,
): { deltaCols: number; deltaSlots: number } => {
  const indexByResourceId = new Map<string, number>();
  resources.forEach((r) => indexByResourceId.set(r.id, r.index));

  let minCol = Number.POSITIVE_INFINITY;
  let maxCol = Number.NEGATIVE_INFINITY;
  let minStart = Number.POSITIVE_INFINITY;
  let maxEnd = Number.NEGATIVE_INFINITY;
  for (const event of sources) {
    const col = indexByResourceId.get(event.resourceId);
    if (col == null) continue;
    minCol = Math.min(minCol, col);
    maxCol = Math.max(maxCol, col);
    minStart = Math.min(minStart, event.startMin);
    maxEnd = Math.max(maxEnd, event.endMin);
  }
  if (!Number.isFinite(minCol)) return { deltaCols, deltaSlots };

  // Column bounds.
  const lo = 0;
  const hi = resources.length - 1;

  const clampedCols = Math.min(Math.max(deltaCols, lo - minCol), hi - maxCol);
  const minDeltaSlots = (cfg.dayStartMin - minStart) / cfg.slotMin;
  const maxDeltaSlots = (cfg.dayEndMin - maxEnd) / cfg.slotMin;
  const clampedSlots = Math.min(
    Math.max(deltaSlots, Math.ceil(minDeltaSlots)),
    Math.floor(maxDeltaSlots),
  );
  return { deltaCols: clampedCols, deltaSlots: clampedSlots };
};

/** Cell position for a pointer location, clamped to the grid. */
export const cellForPoint = (
  cfg: MCGridConfig,
  colCount: number,
  colWidth: number,
  x: number,
  y: number,
): MCCellPos => {
  const resourceIndex = Math.min(
    Math.max(Math.floor(x / Math.max(colWidth, 1)), 0),
    Math.max(colCount - 1, 0),
  );
  const rawMin =
    cfg.dayStartMin + Math.floor(y / cfg.slotHeightPx) * cfg.slotMin;
  return { resourceIndex, slotMin: clampSlot(cfg, rawMin) };
};

// ---------------------------------------------------------------------------
// External drags (shelf / template payloads)
// ---------------------------------------------------------------------------

/** "HH:mm:ss" → minutes; falls back to 30 for missing/garbled payloads. */
export const parseDurationToMin = (duration?: string): number => {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(duration ?? "");
  if (!match) return 30;
  const min = Number(match[1]) * 60 + Number(match[2]);
  return min > 0 ? min : 30;
};

/**
 * Drop validity for an external payload hovering [startMin, endMin) on a
 * column: an existing column, inside the visible day,
 * no solid overlap. Async business checks (credentials, frequency, user
 * active) stay in the bridge — this is only the instant per-cell feedback.
 */
export const validateExternalRange = (
  index: MCEventIndex,
  resources: ReadonlyArray<MCResource>,
  resourceIndex: number,
  startMin: number,
  endMin: number,
  cfg: MCGridConfig,
): boolean => {
  const resource = resources[resourceIndex];
  if (!resource) return false;
  if (startMin < cfg.dayStartMin || endMin > cfg.dayEndMin) return false;
  return !hasSolidOverlap(index, resource.id, startMin, endMin);
};
